import { MSOfficeMimeType } from '@aline/common';
import type { Apollo } from 'src/gql/generated';
import { getWorkflowWasSentForSig } from 'src/workflow';

import { extractAlineDocumentIdFromDocx } from './extract-aline-document-id-from-docx';
import { fetchFilestackBlob } from '../integrations/filestack-download';

export type LookupDocxUploadDocument = (documentId: UUID) => Promise<{
  canEdit: boolean;
  id: UUID;
  isSigned: boolean;
  name: string;
  workflow: Pick<Apollo.Workflow, 'state' | '__typename'> | null;
} | null>;

export async function resolveDocxUploadTarget(options: {
  lookupDocument: LookupDocxUploadDocument;
  bytes?: ArrayBuffer | Uint8Array;
  filename?: string;
  mimetype?: string;
  url?: string;
}): Promise<
  { kind: 'newDocument' } | { document: { id: UUID; name: string }; kind: 'versionCandidate' }
> {
  const { bytes, filename, lookupDocument, mimetype, url } = options;

  if (!getIsLikelyDocx({ filename, mimetype })) {
    return { kind: 'newDocument' };
  }

  try {
    const docxBytes = await resolveDocxBytes({ bytes, url });
    if (!docxBytes) return { kind: 'newDocument' };

    const alineDocumentId = await extractAlineDocumentIdFromDocx(docxBytes);
    if (!alineDocumentId) return { kind: 'newDocument' };

    const document = await lookupDocument(alineDocumentId);
    if (
      !document ||
      !document.canEdit ||
      document.isSigned ||
      getWorkflowWasSentForSig(document.workflow)
    ) {
      return { kind: 'newDocument' };
    }

    return {
      document: { id: document.id, name: document.name },
      kind: 'versionCandidate',
    };
  } catch {
    return { kind: 'newDocument' };
  }
}

function getIsLikelyDocx(options: { filename?: string; mimetype?: string }): boolean {
  const { filename, mimetype } = options;
  if (mimetype === MSOfficeMimeType.DOCX) return true;

  return Boolean(filename?.toLowerCase().endsWith('.docx'));
}

async function resolveDocxBytes(options: {
  bytes?: ArrayBuffer | Uint8Array;
  url?: string;
}): Promise<ArrayBuffer | Uint8Array | null> {
  const { bytes, url } = options;

  if (bytes) return bytes;
  if (!url) return null;

  const blob = await fetchFilestackBlob(url);
  return blob.arrayBuffer();
}
